/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C68 · Typing, mapped onto the arc a pair of eyes can actually resolve.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE GAZE AIMS ALONG A UNIT VECTOR: A TARGET'S DIRECTION MOVES THE EYES, ITS DISTANCE DOES
 * NOT.** Everything here follows from that one fact, and it is why aiming at the real caret does
 * not work. The face sits at the card's centre and the password field runs the card's full width
 * below it, so a short password keeps the caret about 160px left and 120px down — a direction
 * already nearly horizontal, which is the flat part of the arctangent. Ten characters move it from
 * 0.80 to 0.61 of the horizontal excursion: **half a pixel, out of the two and a half the eyes
 * have.** It tracked perfectly and was invisible, which on screen is the same as not running.
 *
 * ⚠️ **Raising `travel` is the obvious knob and it does not fix it**, because it scales both ends
 * equally — it makes a thing nobody can see slightly larger.
 *
 * ⚠️⚠️ **SO THE CARET'S PIXEL IS NOT WHAT THE EYES ARE AIMED AT.** They are aimed at a point that
 * carries the same *information* — how far through the password somebody is — placed where the
 * face can express it. The eyes follow your typing rather than a coordinate, and there is nothing
 * to catch you out with: a face is not a laser pointer, and nothing on screen gives a viewer the
 * reference frame they would need to notice.
 */

/**
 * How many characters sweep the eyes from one end of the arc to the other.
 *
 * ⚠️ **Past it the eyes park at the right-hand end** rather than continuing, which is what running
 * out of arc looks like and is correct: a 40-character passphrase does not deserve twice the
 * expression of a 20-character one.
 */
const FULL_SWEEP = 20;

/**
 * Where to point the eyes for a caret this far into the field.
 *
 * ⚠️⚠️ **THE ARC IS ±45°, AND THAT IS THE SWEET SPOT RATHER THAN A ROUND NUMBER.** The horizontal
 * excursion is `dx / hypot(dx, dy)`, which is most sensitive to `dx` where `|dx| ≈ dy` and
 * saturates either side of it: too narrow an arc and the directions are all nearly straight down
 * and indistinguishable, too wide and they are all nearly horizontal and indistinguishable again.
 * Spanning exactly the drop from the face to the field puts both ends at 45°, which is 1.41 of the
 * excursion end to end — roughly **eight times** what aiming at the true caret produced.
 *
 * ⚠️ **It is measured from the live boxes, not hardcoded**, so it stays right if the card's
 * spacing changes. That matters more than it sounds: the number that was wrong the first two times
 * was a consequence of the layout, and a constant here would be the same mistake written down.
 */
export function typingArcPoint(face: Element, field: HTMLInputElement): { x: number; y: number } {
  const faceBox = face.getBoundingClientRect();
  const fieldBox = field.getBoundingClientRect();

  const faceX = faceBox.left + faceBox.width / 2;
  const faceY = faceBox.top + faceBox.height / 2;
  const y = fieldBox.top + fieldBox.height / 2;

  /* ⚠️ `max(…, 1)` so a collapsed layout — a hidden card, a test with no geometry — cannot make
     the span zero and put every position on top of the face, where the driver's own near-field
     easing takes the excursion to nothing and the eyes stop dead. */
  const span = Math.max(y - faceY, 1);

  /**
   * ⚠️ **`selectionStart`, not the value's length.** They agree while somebody types and stop
   * agreeing the moment an arrow key or a click moves the caret into the middle — which is exactly
   * when eyes still pointing at the end are most obviously not watching.
   */
  const caret = field.selectionStart ?? field.value.length;
  const progress = Math.min(caret / FULL_SWEEP, 1);

  return { x: faceX + span * (progress * 2 - 1), y };
}
